import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import PhenotypeForm from './PhenotypeForm';

/*
 * The phenotype record of an existing collection or intake, in its detail
 * view: surface colours, the flesh's reaction to cutting or pressing, and the
 * rest of the standard description. GET/PUT /{collections|external}/{id}/phenotype.
 *
 * Only the keys the form holds are sent; the route writes exactly those, so
 * this can never blank a field it did not render.
 */
const META = new Set(['exists', 'recorded_by', 'recorded_at', 'updated_at', 'phenotype_id', 'sample_id', 'external_id']);

const clean = data => Object.fromEntries(Object.entries(data || {}).filter(([k, v]) => !META.has(k) && v !== null));

export default function PhenotypeEditor({ anchor, recordId, colourChange, canEdit = true }) {
  const { t } = useTranslation('field');
  const url = `/${anchor}/${recordId}/phenotype`;
  const [value, setValue] = useState({});
  const [status, setStatus] = useState('loading');
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let live = true;
    setStatus('loading');
    Promise.resolve(api.get(url))
      .then(res => { if (live) { setValue(clean(res?.data)); setStatus('ready'); } })
      .catch(err => { if (live) setStatus(err?.response?.status === 404 ? 'ready' : 'failed'); });
    return () => { live = false; };
  }, [url]);

  const save = async () => {
    setStatus('saving');
    setMessage('');
    try {
      const { data } = await api.put(url, value);
      setValue(clean(data));
      setDirty(false);
      setStatus('ready');
      setMessage(t('phenotype.saved'));
    } catch (err) {
      setStatus('ready');
      setMessage(t('phenotype.saveFailed', { error: err?.response?.data?.error || '' }));
    }
  };

  return (
    <details data-testid="phenotype-editor" style={boxStyle}>
      <summary style={summaryStyle} title={t('tip.phenotype.editor')}>{t('phenotype.editorTitle')}</summary>
      {status === 'loading' && <p style={mutedStyle}>{t('phenotype.loading')}</p>}
      {status === 'failed' && <p role="alert" style={mutedStyle}>{t('phenotype.loadFailed')}</p>}
      {(status === 'ready' || status === 'saving') && (
        <fieldset disabled={!canEdit || status === 'saving'} style={{ border: 'none', margin: 0, padding: 0 }}>
          <PhenotypeForm compact value={value} colourChange={colourChange}
                         onChange={next => { setValue(next); setDirty(true); setMessage(''); }} />
          {canEdit && (
            <button type="button" onClick={save} disabled={!dirty || status === 'saving'} title={t('tip.phenotype.save')} style={buttonStyle(dirty)}>
              {status === 'saving' ? t('phenotype.saving') : t('phenotype.save')}
            </button>
          )}
        </fieldset>
      )}
      {message && <p role="status" style={mutedStyle}>{message}</p>}
    </details>
  );
}

const boxStyle = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 12, marginTop: 12, background: 'var(--c-surface)' };
const summaryStyle = { cursor: 'pointer', fontWeight: 600, fontSize: 14, color: 'var(--c-primary-fg)' };
const mutedStyle = { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '8px 0 0' };
const buttonStyle = enabled => ({ marginTop: 12, background: enabled ? 'var(--c-primary)' : 'var(--c-text-faint)', color: 'var(--c-on-accent)',
                                  border: 'none', borderRadius: 6, padding: '7px 16px', cursor: enabled ? 'pointer' : 'default', fontSize: 14 });
